import { createSlice } from '@reduxjs/toolkit';
import { getErrors } from 'api/client';

import { PAYMENT_METHODS, FUEL_OPTIONS } from 'config/app-config';

export type TPaymentMethod = typeof PAYMENT_METHODS[keyof typeof PAYMENT_METHODS];
type TFuelType = typeof FUEL_OPTIONS[keyof typeof FUEL_OPTIONS];

export type TTariffList = {
  id: string;
  displayName: string;
  isTOU: boolean;
};

export type TTariff = {
  annualConsumption: number;
  id: string;
  standingCharge: number;
  unitRate: number | null;
  unitRateDay: number | null;
  unitRateNight: number | null;
  unitRateDayName: string | null;
  unitRateNightName: string | null;
  estimatedPersonalProjection: number;
  estimatedAnnualConsumption: number;
  endDate: string;
  meterRate: string;
  postcode: string;
  personalProjection: number;
  tariffComparisonRate: number | null;
  fuel: TFuelType;
};

export type TTariffDetails = {
  additionalProducts: string;
  discounts: string;
  endDate: string;
  exitFee: string;
  id: string;
  paymentType: TPaymentMethod;
  priceGuaranteedDate: string;
  supplierName: string;
  tariffName: string;
  tariffType: string;
  termsAndConditions: string;
  touDefinition: string | null;
  tariffs: TTariff[];
};

export type TState = {
  tariffDetails: TTariffDetails | null;
  tariffList: TTariffList[];
  isLoadingTariffList: boolean;
  isLoadingTariffDetails: boolean;
  tariffListErrors: ReturnType<typeof getErrors>;
  tariffDetailsErrors: ReturnType<typeof getErrors>;
  currentTariff: TTariffList | null;
  isEconomy7: boolean;
  postcode: string;
  paymentMethod: TPaymentMethod;
  fuelType: TFuelType;
};

export const initialState: TState = {
  tariffDetails: null,
  tariffList: [],
  isLoadingTariffList: false,
  isLoadingTariffDetails: false,
  tariffListErrors: null,
  tariffDetailsErrors: null,
  currentTariff: null,
  isEconomy7: false,
  postcode: '',
  paymentMethod: PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT,
  fuelType: FUEL_OPTIONS.DUAL
};

export const slice = createSlice({
  name: 'tariffInformation',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.tariffDetails = initialState.tariffDetails;
      state.tariffList = initialState.tariffList;
      state.isLoadingTariffList = initialState.isLoadingTariffList;
      state.isLoadingTariffDetails = initialState.isLoadingTariffDetails;
      state.tariffDetailsErrors = initialState.tariffDetailsErrors;
      state.tariffListErrors = initialState.tariffListErrors;
      state.currentTariff = initialState.currentTariff;
      state.isEconomy7 = initialState.isEconomy7;
      state.postcode = initialState.postcode;
      state.paymentMethod = initialState.paymentMethod;
      state.fuelType = initialState.fuelType;
    },
    setIsLoadingTariffList: (state) => {
      state.isLoadingTariffList = true;
      state.tariffListErrors = initialState.tariffListErrors;
    },
    setIsLoadingTariffDetails: (state) => {
      state.isLoadingTariffDetails = true;
      state.tariffDetailsErrors = initialState.tariffDetailsErrors;
    },
    resetTariffDetails: (state) => {
      state.tariffDetails = initialState.tariffDetails;
      state.isLoadingTariffDetails = initialState.isLoadingTariffDetails;
      state.tariffDetailsErrors = initialState.tariffDetailsErrors;
    },
    setSuccessTariffDetails: (state, { payload }) => {
      state.isLoadingTariffDetails = false;
      state.tariffDetailsErrors = initialState.tariffDetailsErrors;
      state.tariffDetails = payload.data;
    },
    setSuccessTariffList: (state, { payload }) => {
      state.isLoadingTariffList = false;
      state.tariffListErrors = initialState.tariffListErrors;
      state.tariffList = payload.data;
    },
    resetTariffList: (state) => {
      state.tariffList = initialState.tariffList;
      state.isLoadingTariffList = initialState.isLoadingTariffList;
      state.tariffListErrors = initialState.tariffListErrors;
    },
    setTariffDetailsError: (state, { payload }) => {
      state.isLoadingTariffList = false;
      state.isLoadingTariffDetails = false;
      state.tariffDetailsErrors = payload;
    },
    setTariffListError: (state, { payload }) => {
      state.isLoadingTariffList = false;
      state.isLoadingTariffDetails = false;
      state.tariffListErrors = payload;
    },
    setCurrentTariff: (state, { payload }) => {
      state.currentTariff = payload;
    },
    resetCurrentTariff: (state) => {
      state.currentTariff = initialState.currentTariff;
    },
    setIsEconomy7: (state, { payload }) => {
      state.isEconomy7 = payload;
    },
    setPostcode: (state, { payload }) => {
      state.postcode = payload;
    },
    setPaymentMethod: (state, { payload }) => {
      state.paymentMethod = payload;
    },
    setFuelType: (state, { payload }) => {
      state.fuelType = payload;
    }
  }
});

export const {
  setInitialState,
  setSuccessTariffDetails,
  setTariffDetailsError,
  setTariffListError,
  setIsLoadingTariffList,
  setIsLoadingTariffDetails,
  setIsEconomy7,
  setCurrentTariff,
  setSuccessTariffList,
  setPostcode,
  setPaymentMethod,
  setFuelType,
  resetTariffDetails,
  resetCurrentTariff,
  resetTariffList
} = slice.actions;

export const selectSlice: (state) => TState = (state) => state[slice.name];
export const selectTariffDetails = (state) => selectSlice(state).tariffDetails;
export const selectTariffByFuel: (state, fuel: TFuelType) => TTariff | null = (
  state,
  fuel
) => {
  return (
    selectTariffDetails(state)?.tariffs.find(
      (tariff) => tariff.fuel === fuel
    ) || null
  );
};
export const selectGasTariff = (state) => selectTariffByFuel(state, 'gas');
export const selectElectricityTariff = (state) =>
  selectTariffByFuel(state, 'electricity');
export const selectTariffList = (state) => selectSlice(state).tariffList;
export const selectIsLoadingTariffList = (state) =>
  selectSlice(state).isLoadingTariffList;
export const selectIsLoadingTariffDetails = (state) =>
  selectSlice(state).isLoadingTariffDetails;
export const selectTariffDetailsErrors = (state) =>
  selectSlice(state).tariffDetailsErrors;
export const selectTariffListErrors = (state) =>
  selectSlice(state).tariffListErrors;
export const selectIsEconomy7 = (state) => selectSlice(state).isEconomy7;
export const selectCurrentTariff = (state) => selectSlice(state).currentTariff;
export const selectPostcode = (state) => selectSlice(state).postcode;
export const selectPaymentMethod = (state) => selectSlice(state).paymentMethod;
export const selectFuelType = (state) => selectSlice(state).fuelType;

export default slice.reducer;
